import {deserialize, DrupalNode, DrupalSearchApiFacet} from "next-drupal";
import {Dictionary, DrupalSearchApiResponseMeta} from "@/types/hygiena-types";
import {SearchHero} from "@/components/organisms/hero/search-hero";
import {createRef, useContext, useEffect, useRef, useState} from "react";
import {getKvpPair} from "@/lib/get-kvp-pair";
import {useRouter} from "next/router";
import {searchIndex} from "@/lib/search-index";
import {DrupalJsonApiParams} from "drupal-jsonapi-params";
import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";

const Divider = dynamic(() => import("../../atoms/divider").then((mod) => mod.Divider));
const Button = dynamic(() => import("../../atoms/button").then((mod) => mod.Button));
const LoadingSpinnerOverlay = dynamic(() => import("../../atoms/loading-spinner").then((mod) => mod.LoadingSpinnerOverlay));
const Pagination = dynamic(() => import("../../organisms/widget--pagination").then((mod) => mod.Pagination));
const EventTeaser = dynamic(() => import("../nodes/node--event--teaser").then((mod) => mod.EventTeaser));

interface EventsPageProps {
  node: DrupalNode,
  events?: DrupalNode[];
  highlightedEvents?: DrupalNode[];
  meta?: DrupalSearchApiResponseMeta,
  facets?: DrupalSearchApiFacet[];
}
export function EventsPage({ node, events, highlightedEvents, meta, facets: initialFacets }: EventsPageProps) {
  const ready = useRef(false);
  const router = useRouter();
  const heroHeadline = getKvpPair(node, "hero_headline", "long")?.value;
  const [facets, setFacets] = useState<DrupalSearchApiFacet[]>(initialFacets ?? []);
  const grouping = {} as {[key: string]: DrupalNode[]};
  const [eventCount, setEventCount] = useState(meta?.count ?? 0);
  const [results, setResults] = useState(events?.filter(event => isEventActive(event)));
  const [pageLimit, setPageLimit] = useState(10);
  const [eventType, setEventType] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState<boolean>();
  const t = useContext<Dictionary>(DictionaryContext);
  const scrollRef = createRef<HTMLDivElement>();
  const activeHighlighted = highlightedEvents ?? [];

  function isEventActive (event: any): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDateStr = event.field_event_start_date; // "YYYY-MM-DD"
    const endDateStr = event.field_event_end_date;     // "YYYY-MM-DD" or null/undefined
    const startDate = new Date(startDateStr);
    if (!endDateStr) {
      return startDate >= today || startDate.toDateString() === today.toDateString();
    }
    const endDate = new Date(endDateStr);
    return endDate >= today;
  };

  /**
   * A sort function for the month/year event grouping.
   *
   * @param a
   * @param b
   */
  const sortMonthYear = (a: string, b: string): number => {
    const [aM, aY] = a.split("/");
    const [bM, bY] = a.split("/");
    if (aM < bM && aY < bY) return 1;
    if (aM > bM && aY > bY) return -1;
    return 0;
  }

  async function updateFacets() {
    let response;
    let results;
    const params = new DrupalJsonApiParams()
      .addGroup("startDate", "AND", "endDate")
      .addGroup("endDate", "OR", "parent")
      .addGroup("parent", "AND")
      .addFilter("status", "1", "=", "parent")
      .addFilter("field_event_start_date", new Date().toISOString().split("T")[0], ">", "endDate")
      .addFilter("field_event_end_date", new Date().toISOString().split("T")[0], ">", "endDate")
      .addFilter("field_event_start_date", new Date().toISOString().split("T")[0], ">", "startDate")
      .addFilter("field_event_end_date", "", "=", "startDate")
      .addPageLimit(pageLimit)
      .addPageOffset(pageLimit * page)
      .addInclude([
        "field_event_image.image",
        "field_event_image.bf_image",
        "field_event_type",
      ])
      .addSort("field_event_start_date", "ASC");
    if (router?.locale && router.locale !== router?.defaultLocale) {
      params.addFilter("language_with_fallback", router.locale, "=", "parent")
    }
    else if (router?.defaultLocale) {
      params.addFilter("langcode", router.defaultLocale, "=", "parent")
    }
    if (eventType) {
      params.addFilter("field_event_type", eventType);
    }

    try {
      response = await searchIndex("events", params.getQueryString(), router.locale, router.defaultLocale);
      response = response.ok ? response.data : {};
      results = response?.data ? await deserialize(response) as DrupalNode[] : [];
    }
    catch (error) {
    }

    setResults(results ?? []);
    await setEventCount(response?.meta?.count ?? 0);
    await setFacets(response?.meta?.facets ?? []);
    await setLoading(false);
  }

  useEffect(() => {
    if (!ready?.current) {
      ready.current = true;
      return;
    }
    setLoading(true);
    updateFacets();
  }, [page, eventType, router.locale]);

  async function changePage(page) {
    await setLoading(true);
    await setPage(page);
  }

  if (Array.isArray(results)) {
    results.map(item => {
      const date = new Date(item?.field_event_start_date);
      const group = (date.getMonth() + 1) + "/" + date.getFullYear();
      if (!grouping[group]) {
        grouping[group] = [];
      }
      grouping[group].push(item);
    });
  }

  return (
    <>
      <SearchHero title={node?.title} image={node?.field_kvp_hero_image} small={true} headline={heroHeadline} />
      <div className="container pt-12 px-6 mx-auto">
        {activeHighlighted?.length ? (
          <div className="container pb-10 mx-auto text-black">
            <div className="flex flex-wrap flex-col gap-4">
              <h3 className="md:!text-4xl !font-light">{`${t?.all?.highlighted_events}`}</h3>
              <Divider className="!my-4"/>
              {activeHighlighted.map(item => (
                <EventTeaser key={item.id} node={item}/>
              ))}
            </div>
          </div>
        ): <></>}
      </div>
      <div className="container max-md:pb-6 px-6 pt-12 mx-auto">
        <div className="order-first md:order-last md:top-0">
          {facets.map((facet, key) => {
            if (facet?.terms && facet?.terms.length > 1) {
              return (
                <div key={key}>
                  <div className="text-center text-black pb-12">
                    <div className="text-center flex flex-wrap gap-2 justify-start lg:justify-end">
                      <Button
                        invert={!!(eventType && eventType.length > 0)}
                        className={"font-normal !px-6"}
                        color={!eventType ? "primary" : "black"}
                        callback={() => setEventType("")}>{`${t?.search?.all}`}</Button>
                      {facet.terms.map(term => (
                        <Button
                          key={term.values.value}
                          invert={term.values.value !== eventType}
                          className={"font-normal !px-6"}
                          color={term.values.value === eventType ? "primary" : "black"}
                          callback={() => setEventType(term.values.value)}>{term.values.label}</Button>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }
          })}
        </div>
      </div>
      <div className="relative scroll-m-24" ref={scrollRef}>
        <LoadingSpinnerOverlay loading={loading} fixed={true}/>
        {results?.length ? (
          <div className="container px-6 pb-10 mx-auto text-black">
            {Object.keys(grouping).sort(sortMonthYear).map(key => (
              <div key={key} className="flex flex-wrap flex-col gap-4 mb-16">
                <h3 className="md:!text-4xl !font-light">{
                  new Date(`${key.split("/")[0]}/1/${key.split("/")[1]}`)
                    .toLocaleString(router.locale ?? 'en-US', {month: "long", year: "numeric"})
                }</h3>
                <Divider className="!my-4"/>
                {grouping[key].map(item => (
                  <EventTeaser key={item.id} node={item}/>
                ))}
              </div>
            ))}
          </div>
        ) : <div className="container mx-auto"><p>{t?.all?.no_events}</p></div>}
        {results?.length ? (
          <div className="container mx-auto">
            <Pagination count={eventCount} page={page} limit={pageLimit} changePage={changePage} radius={8}
                        scrollRef={scrollRef}/>
          </div>
        ) : <></>}
      </div>
    </>
  );
}

